import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { getLangDirection } from "../../../config/i18n";
import { SupportedLanguage } from "../../../interfaces";
import { normalizeLanguage, resolveRoomTextLang } from "../utils/langUtils";
import { roomContentLangStore } from "../stores/roomContentLangStore";
import { useStore } from "../../../lib/store";

export function useActiveLang(): {
  activeLang: SupportedLanguage;
  contentLang: SupportedLanguage;
  roomTextLang: SupportedLanguage;
  isRtl: boolean;
  changeActiveLang: (lang: SupportedLanguage) => void;
} {
  const { i18n } = useTranslation();
  const activeLang = normalizeLanguage(
    i18n.resolvedLanguage ?? navigator.language
  );
  const [roomContentLang] = useStore(roomContentLangStore);
  const contentLang: SupportedLanguage =
    roomContentLang !== null
      ? roomContentLang.language
      : activeLang === "fr"
        ? "fr"
        : "en";
  // The language for a ROOM'S OWN TEXT — its name and description.
  //
  // Distinct from `contentLang`, and the difference is not cosmetic. `contentLang`
  // falls back to an fr/en GUESS because its consumers pass it to the api as a
  // content `language` filter, where fr/en is the meaningful corpus split. For room
  // TEXT that guess is a bug: `LangSync` sets `roomContentLang` to null whenever the
  // resolved room language equals the user's own (the common default), so a German
  // reader in a room that allows German would resolve to `en` and be shown the
  // English name and description even when the German ones are authored.
  //
  // So: the reader's explicit in-room choice, else their interface locale. Never a
  // guess. Room text has no corpus to split — it is eight strings on one record.
  //
  // ── CONGRESS SURFACES read `contentLang`, NOT `roomTextLang` ──────────────
  // This is the one case that looks like it should follow the room-text rule and
  // must not, so it is written down here rather than restated in six files.
  //
  // A ROOM is eight strings on one record with no corpus behind them, so its text
  // cannot follow a corpus axis. A CONGRESS SPACE **is** a corpus, and the api has
  // already scoped it to one language: `topSessionsTitles`, `curatorTranslations`,
  // `topSessions` and the visibility gate on every block are all filtered by the
  // `language` query param — which is `contentLang`. So the congress name, place,
  // date, countdown units and live pill follow `contentLang` too: the card's text
  // must follow the corpus it titles, or the card reads mixed — a German name over
  // an English sessions list and an English admin-authored heading.
  //
  // `congress.translations` IS returned in full, so `roomTextLang` was available
  // and was rejected on that evidence, not on preference. The chrome AROUND a
  // congress card (section titles, CTA labels, "Curator's note", tap hints) stays
  // on the interface locale through bare `t()`.
  const roomTextLang: SupportedLanguage = resolveRoomTextLang(
    roomContentLang,
    activeLang
  );
  const isRtl = getLangDirection(activeLang) === "rtl";
  const changeActiveLang = useCallback(
    (lang: SupportedLanguage) => {
      void i18n.changeLanguage(lang);
    },
    [i18n]
  );
  return { activeLang, contentLang, roomTextLang, isRtl, changeActiveLang };
}
